import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';

/**
 * EditCycleModal - Admin-only edit dialog for any cycle (active, draft_ready, or closed).
 * All fields editable except status. Edit reason mandatory; saved to audit log.
 */
export const EditCycleModal = ({ cycle, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!cycle) return;
    setForm({
      name: cycle.name || '',
      type: cycle.type || 'monthly',
      start_date: cycle.start_date || '',
      end_date: cycle.end_date || '',
      default_profit_percent: cycle.default_profit_percent ?? '',
      inr_to_usdt_rate: cycle.inr_to_usdt_rate ?? '',
      aed_to_usdt_rate: cycle.aed_to_usdt_rate ?? '',
      conversion_rate_used: cycle.conversion_rate_used ?? '',
      total_profit_made_usdt: cycle.total_profit_made_usdt ?? '',
      total_distributed_usdt: cycle.total_distributed_usdt ?? '',
      company_retention_usdt: cycle.company_retention_usdt ?? '',
      aum_at_close_usdt: cycle.aum_at_close_usdt ?? '',
      effective_client_rate: cycle.effective_client_rate ?? '',
      effective_company_rate: cycle.effective_company_rate ?? '',
    });
    setReason('');
  }, [cycle]);

  if (!cycle) return null;

  const numericFields = [
    'default_profit_percent', 'inr_to_usdt_rate', 'aed_to_usdt_rate',
    'conversion_rate_used', 'total_profit_made_usdt', 'total_distributed_usdt',
    'company_retention_usdt', 'aum_at_close_usdt', 'effective_client_rate',
    'effective_company_rate',
  ];

  // Build only the changed fields to send
  const buildPayload = () => {
    const payload = { edit_reason: reason.trim() };
    Object.keys(form).forEach((k) => {
      const original = cycle[k];
      const current = form[k];
      if (current === '' || current === null || current === undefined) return;
      if (numericFields.includes(k)) {
        const num = parseFloat(current);
        if (!Number.isNaN(num) && num !== original) payload[k] = num;
      } else if (current !== original) {
        payload[k] = current;
      }
    });
    return payload;
  };

  const handleSave = async () => {
    if (!reason.trim()) {
      toast.error('Edit reason is required');
      return;
    }
    const payload = buildPayload();
    if (Object.keys(payload).length <= 1) {
      toast.error('No changes to save');
      return;
    }
    setSaving(true);
    try {
      await axiosInstance.put(`/cycles/${cycle.id}`, payload);
      toast.success('Cycle updated');
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update cycle');
    } finally {
      setSaving(false);
    }
  };

  const fld = (label, key, type = 'text', extra = {}) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        type={type}
        value={form[key] ?? ''}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="form-input w-full text-sm"
        data-testid={`edit-cycle-${key}`}
        {...extra}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-5 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold">Edit Cycle: {cycle.name}</h2>
            <p className="text-xs text-gray-500">Status: <span className="font-semibold">{cycle.status}</span> · All fields editable except status</p>
          </div>
          <button onClick={onClose} data-testid="edit-cycle-close" className="text-gray-500 hover:text-gray-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fld('Name', 'name')}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
              <select
                value={form.type || 'monthly'}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="form-input w-full text-sm"
                data-testid="edit-cycle-type"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="half_yearly">Half-Yearly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
            {fld('Start Date', 'start_date', 'date')}
            {fld('End Date', 'end_date', 'date')}
            {fld('Default Profit %', 'default_profit_percent', 'number', { step: '0.01' })}
            {fld('INR → USDT Rate', 'inr_to_usdt_rate', 'number', { step: '0.01' })}
            {fld('AED → USDT Rate', 'aed_to_usdt_rate', 'number', { step: '0.01' })}
            {fld('Conversion Rate Used', 'conversion_rate_used', 'number', { step: '0.01' })}
          </div>

          <div className="border-t pt-4">
            <p className="text-xs font-semibold text-gray-700 mb-2">P&L Snapshot (closed cycles only)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {fld('Total Profit Made (USDT)', 'total_profit_made_usdt', 'number', { step: '0.01' })}
              {fld('Total Distributed (USDT)', 'total_distributed_usdt', 'number', { step: '0.01' })}
              {fld('Company Retention (USDT)', 'company_retention_usdt', 'number', { step: '0.01' })}
              {fld('AUM at Close (USDT)', 'aum_at_close_usdt', 'number', { step: '0.01' })}
              {fld('Effective Client Rate %', 'effective_client_rate', 'number', { step: '0.01' })}
              {fld('Effective Company Rate %', 'effective_company_rate', 'number', { step: '0.01' })}
            </div>
          </div>

          <div className="border-t pt-4">
            <label className="block text-xs font-medium text-red-600 mb-1">Edit Reason <span className="text-red-500">*</span></label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="edit-cycle-reason"
              rows={2}
              className="form-input w-full text-sm"
              placeholder="Why are you editing this cycle? (required for audit log)"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t sticky bottom-0 bg-white">
          <button onClick={onClose} className="btn-secondary text-sm" data-testid="edit-cycle-cancel">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary text-sm"
            data-testid="edit-cycle-save"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};
